import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHero = !scrolled && location.pathname === "/" && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.lenis) window.lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 350);
    } else {
      scroll();
    }
  };

  const links = [
    { id: "services", label: t.nav.services },
    { id: "before-after", label: t.nav.beforeAfter },
    { id: "process", label: t.nav.process },
    { id: "reviews", label: t.nav.reviews },
    { id: "faq", label: t.nav.faq },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? "glass-panel" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group shrink-0">
          <img
            src="/logos/emberline-final-512.png"
            alt="Emberline Chimney Co. logo"
            className="w-11 h-11 sm:w-12 sm:h-12 object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-md"
          />
          <span className={`font-tech font-semibold tracking-[0.22em] text-sm sm:text-base transition-colors duration-300 ${onHero ? "text-white" : "text-neutral-900"}`}>
            EMBERLINE
          </span>
        </Link>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-6 px-6 min-w-0">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goTo(l.id)}
              className={`nav-underline font-tech text-xs tracking-[0.15em] uppercase whitespace-nowrap transition-colors duration-200 ${onHero ? "text-white/70 hover:text-white" : "text-neutral-500 hover:text-[#ff6b00]"}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className={`hidden sm:flex items-center rounded-full border overflow-hidden transition-colors duration-300 ${onHero ? "border-white/30 bg-white/10" : "border-neutral-300 bg-white/60"}`}>
            {["en", "es"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 font-tech text-[11px] tracking-widest uppercase transition-colors duration-200 ${lang === l ? "bg-[#ff6b00] text-white" : onHero ? "text-white/70 hover:text-white" : "text-neutral-500 hover:text-neutral-900"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="tel:+12162784778"
            data-testid="navbar-phone-link"
            className={`hidden md:flex items-center gap-2 font-tech text-xs whitespace-nowrap transition-colors duration-200 ${onHero ? "text-white/80 hover:text-white" : "text-neutral-600 hover:text-[#ff6b00]"}`}
          >
            <Phone size={14} className="text-[#ff6b00]" /> (216) 278-4778
          </a>
          <button
            data-testid="navbar-book-btn"
            onClick={() => goTo("booking")}
            className="btn-fire hidden sm:block rounded-full px-5 py-2 font-tech text-xs font-semibold tracking-widest uppercase text-white"
          >
            {t.nav.book}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 transition-colors duration-300 ${onHero ? "text-white" : "text-neutral-800"}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass-panel border-t border-neutral-200 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => goTo(l.id)}
              className="text-left font-tech text-sm tracking-widest uppercase text-neutral-600 hover:text-[#ff6b00]"
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-2 pt-2">
            {["en", "es"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-4 py-2 rounded-full font-tech text-xs tracking-widest uppercase border ${lang === l ? "bg-[#ff6b00] border-[#ff6b00] text-white" : "border-neutral-300 text-neutral-500"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => goTo("booking")}
            className="btn-fire rounded-full px-5 py-3 font-tech text-xs font-semibold tracking-widest uppercase text-white"
          >
            {t.nav.book}
          </button>
        </div>
      )}
    </header>
  );
};
